/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PagedResults } from "app/models/paged-results";
import { UserMmcInfo } from "app/models/mmc/user-mmc-info";
import { ImageService } from "app/services/image.service";
import { MmcService } from "app/services/mmc.service";
import { SetAffiliateDialogComponent } from "./set-affiliate-dialog/set-affiliate-dialog.component";
import { SimpleIndefiniteProgressDialogComponent } from "../simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";

import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged} from "rxjs/operators";

export const UsersMmcInfoComponent_PageSize = 10;

@Component({
  templateUrl: './users-mmc-info.component.html',
  styleUrls: ['./users-mmc-info.component.css']
})
export class UsersMmcInfoComponent implements OnInit {

  /** Set Affiliate Dialog used / displayed when updating the MMC Affiliate for the selected Users */
  @ViewChild(SetAffiliateDialogComponent) setAffiliateDialog: SetAffiliateDialogComponent;

  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;


  /* 
   * Table Data / Paging Variables
   */
  /** User MMC Info for the Table Data */
  userMmcInfos: UserMmcInfo[] = [];
  /** Flag holding whether a page is being loaded / changed to. Used to disable some UI controls during the transition. */
  isPageBeingChanged = false;
  /** Total Number of Users for the paging controls logic */
  pagingTotalDataCount = 0;
  /** Current Page Number being displayed. */
  pagingCurrentPageNumber = 1;
  /** Number of Users to display per page */
  pagingPageSize = UsersMmcInfoComponent_PageSize;

  /** Map that hold the ID of the Users selected in the UI */
  selectedItems: Map<number, UserMmcInfo> = new Map();

  /** Possible Affiliates for Dialog.  Loaded / Cached during the page loading to improve performance. */
  mmcAffiliates: string[] = [];

  /** Possible Filter Value.  When NULL, Filter is not shown. */
  filterValue: string = null;

  /** Subject representing changes to the Filter Value */
  filterValueChange = new Subject<string>();



  /** Constructor */
  constructor(
    private route: ActivatedRoute,
    private mmcService: MmcService,
    private imageService: ImageService
  ) { }

  /** Initializes the component */
  ngOnInit(): void {

    this.filterValueChange.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => this.filterValueChanged(value));

    this.route.data.subscribe((
      data: {
        userMmcInfo: PagedResults<UserMmcInfo>,
        mmcAffiliates: string[]
      }) => {
        // Update the displayed page data with the data retrieved during the page load.
        this._updatePageData(data.userMmcInfo.data, data.userMmcInfo.size, 1); // Shows the 1st Page of Data originally

        // Update the cached possible MMC Affiliates that Users can be set to
        while (this.mmcAffiliates.length > 0) {
          this.mmcAffiliates.pop();
        }
        if (data?.mmcAffiliates?.length > 0) {
          this.mmcAffiliates.push(...data.mmcAffiliates);
        }
      });

    // Nothing is selected by default, so clear the map. (Might not be needed, but doing it anyways)
    this.selectedItems.clear();
  }

  /**
   * Updates the displayed paged table data and any paging
   * variables required using the given information. 
   */
  private _updatePageData(data: UserMmcInfo[], totalDataCount: number, pageNumber: number): void {
    // Update the Data array
    while(this.userMmcInfos.length > 0) {
      this.userMmcInfos.pop();
    }
    if (data?.length > 0) {
      this.userMmcInfos.push(...data);
    }

    // Update the Paging Variables
    this.pagingTotalDataCount = totalDataCount;
    this.pagingCurrentPageNumber = pageNumber;

    /*
     * Also update any cached references to the selected Mmc Infos,
     * otherwise the lookup for the Affiliate to be selected by default
     * for the Set Affiliate Dialog could select an old value. 
     */
    this.userMmcInfos.forEach(mmcInfo => {
      if (this.selectedItems.has(mmcInfo.user_id)) {
        this.selectedItems.set(mmcInfo.user_id, mmcInfo);
      }
    });
  }

  /**
   * Called when the Table Data Page is changed by the UI. 
   * Loads the Data for the Page and updates the display.
   */
  pageChanged(pageNumber: number): void {
    if (!this.isPageBeingChanged) {
      this.isPageBeingChanged = true;
      this._pageChanged(pageNumber);
    }
  }

  /**
   * Called when the UI's Table Data is being changed to display
   * a new / fresh page of data.   
   * 
   * Updates the Table Data along with any paging variables.
   */
  private async _pageChanged(pageNumber: number): Promise<void> {
    // Query for the new Page Information
    let data: PagedResults<UserMmcInfo> = null;
    try {
      data = await this.mmcService.getUsersMmcInfo((pageNumber - 1), this.pagingPageSize, this.filterValue);
    } catch (error) {
      console.error(`Problem obtaining User MMC Infos for Page: ${pageNumber}!`, error);
    }

    // Update the Page Data
    if (data != null) {
      this._updatePageData(data.data, data.size, pageNumber);
    }

    // Update the Flag indicating the Page Data is being changed
    this.isPageBeingChanged = false;
  }

  /**
   * Returns the Image Source to use for the given UserMmcInfo.
   * This is used by the UI to display the Image for the User.
   */
  getImageSource(userMmcInfo: UserMmcInfo) {
    if (userMmcInfo?.user_image_id != null) {
      return this.imageService.getImageSourceForImageId(userMmcInfo.user_image_id);
    } else {
      return 'assets/images/userDefault.png';
    }
  }

  /**
   * Returns a boolean indicating if any Items have been selected
   * in the table.  This is used by the UI to disable some actions
   * until something is selected.
   */
  isAnyItemSelected(): boolean {
    // If an Item was selected, the Map would contain an entry
    return this.selectedItems.size > 0;
  }

  /**
   * Called when an Item in the Table Data is clicked on to 
   * be selected / unselected and handles the event.
   */
  onCheckboxClicked(mmcInfo: UserMmcInfo, event: any): void {
    // Figure out if the Item is being selected or not...
    const selected: boolean = event != null && event.target != null && event.target.checked != null? event.target.checked : false;
    
    // Either select / unselect the Item by adding it / removing it from the Map
    if (selected) {
      this.selectedItems.set(mmcInfo.user_id, mmcInfo);
    } else {
      this.selectedItems.delete(mmcInfo.user_id);
    }
  }

  /**
   * Returns a boolean indicating if the given UserMmcInfo
   * was selected by the User or not. Used by the UI to determine
   * if an Item being displayed was previously selected.
   */
  isItemSelected(mmcInfo: UserMmcInfo): boolean {
    return this.selectedItems.has(mmcInfo.user_id);
  }

  /**
   * Called by the UI when the "Set Affiliate" button is clicked.
   * 
   * Displays the "Set Affiliate Dialog" that allows User to select the MMC Affiliate to
   * set to the selected Users.
   */
  onSetAffiliateClicked(): void {
    console.log(`Set Affiliate Clicked!`);

    // Get the MMC Affiliate that should be selected by default on the Set Affiliate Dialog
    let selectedAffiliate = null;
    {
      /*
       * Only pre-select an Affiliate if all the selected Users share the same
       * Affiliate value, if not just use NULL / None. 
       */
      let i = 0;
      for (let selected of this.selectedItems.values()) {
        if (i == 0) {
          if (selected.mmc_affiliate != null && selected.mmc_affiliate.length > 0) {
            selectedAffiliate = selected.mmc_affiliate;
          } else {
            break;
          }
        } else if (selectedAffiliate != null) {
          if (selectedAffiliate != selected.mmc_affiliate) {
            selectedAffiliate = null;
            break;
          }
        }
        i ++;
      }
    }

    // Display the Set Affiliate Dialog
    this.setAffiliateDialog.show(selectedAffiliate);
  }

  /**
   * Called when the "Update" button is clicked on the "Set Affiliate Dialog".  
   * This sets the given affiliate value (assumed to be the selected value in the Dialog)
   * to the Users that were selected, then reloads the page. 
   */
  async onSetAffiliateUpdateClicked(affiliate: string): Promise<void> {    
    // Disable the controls on the SetAffiliateDialog...
    this.setAffiliateDialog.uiDisabled = true;
    
    // Display the IndefiniteProgressDialog
    this.indefiniteProgressDialog.show('Processing', '');

    // Handle the Set MMC Affiliate call
    try {
      const userIds: number[] = [];
      userIds.push(...this.selectedItems.keys());
      await this.mmcService.setUsersMmcAffiliate(userIds, affiliate);
    } catch (error) {
      console.error(`Problem setting Affiliate Name to selected Users.`, error);
      this.indefiniteProgressDialog.hide();
      return;
    }

    // Reload the current page of data
    this.isPageBeingChanged = true;
    await this._pageChanged(this.pagingCurrentPageNumber);
    
    // Hide the progress dialog / set affilite dialogs
    this.indefiniteProgressDialog.hide();
    this.setAffiliateDialog.hide();
  }

  /**
   * Called by UI when the User clicks on the "User ID" Filter to 
   * add it to the screen / start Filtering Users by the "User ID"
   */
  addFilter(): void {
    this.filterValue = "";
  }

  /**
   * Called by UI when the User clicks the "Remove filter" option.
   * This removes the current Filter and reloads the page data. 
   */
  removeFilter(): void {
    const reloadPage = this.filterValue != null && this.filterValue.length > 0;
    this.filterValue = null;
    if (reloadPage) {
      this.pageChanged(1);
    }
  }

  /**
   * Called when the Filter Value is changed with the new Filter Value.
   * This searches the Users and updates the page. 
   */
  filterValueChanged(filterValue: string): void {
    this.pageChanged(1);
  }

}